import React from 'react';
import { Document, Text, StyleSheet } from '@react-pdf/renderer';
import type { RevenueReport } from '../../../services/revenueService';
import {
  Page, FilterStrip, StatStrip, DataTable,
  pdfColors, pdfSpacing, fmtDate, fmtMoney,
  type DataTableColumn, type SchoolBranding,
} from '../../../utils/pdf';

type Row = RevenueReport['rows'][number];

interface Props {
  report: RevenueReport;
  currency: string;
  branding: SchoolBranding;
  generatedAt: string;
}

const styles = StyleSheet.create({
  muted: { color: pdfColors.textMuted },
  voucher: { fontWeight: 600 },
  received: { color: pdfColors.success, fontWeight: 600 },
  empty: { marginTop: pdfSpacing.md, textAlign: 'center', fontSize: 9, color: pdfColors.textMuted, fontStyle: 'italic' },
});

/**
 * The Revenue Report — money received outside fees, as the Revenue screen
 * filters it: the letterhead, what was received and how many entries, then
 * each entry with its voucher. Reversed entries, when included, stay in
 * the list greyed and say so.
 */
const RevenueReportDocument: React.FC<Props> = ({ report, currency, branding, generatedAt }) => {
  const money = (n: number) => fmtMoney(n, currency);
  const dim = (r: Row) => (r.reversed ? styles.muted : undefined);
  const columns: Array<DataTableColumn<Row>> = [
    { key: 'n', header: '#', width: 24, align: 'center', render: (_r, i) => <Text style={styles.muted}>{i + 1}</Text> },
    { key: 'date', header: 'Date', width: 62, render: r => r.date },
    { key: 'voucher', header: 'Voucher', width: 92, render: r => <Text style={styles.voucher}>{r.voucher || '—'}</Text> },
    { key: 'title', header: 'Title', flex: 1.6, render: r => <Text style={dim(r)}>{r.reversed ? `${r.title} (reversed)` : r.title}</Text> },
    { key: 'category', header: 'Category', width: 84, render: r => r.category || '—' },
    { key: 'from', header: 'Received from', flex: 1.4, render: r => r.from || '—' },
    { key: 'method', header: 'Method', flex: 1.1, render: r => `${r.method}${r.reference ? ` · ${r.reference}` : ''}` },
    { key: 'amount', header: 'Amount', width: 84, align: 'right', render: r => <Text style={r.reversed ? styles.muted : styles.received}>{money(r.amount)}</Text> },
  ];

  return (
    <Document title="Revenue Report" author={branding.name}>
      <Page
        branding={branding}
        orientation="landscape"
        meta={{ title: 'Revenue Report', subtitle: report.includeReversed ? 'Including reversed entries' : undefined, generatedAt }}
        official={{ details: [
          { label: 'Records', value: String(report.rows.length) },
          { label: 'Generated', value: fmtDate(generatedAt) },
        ] }}
      >
        {report.includeReversed && <FilterStrip summary="Including reversed entries" />}
        <StatStrip
          dense
          items={[
            { label: 'Received', value: money(report.received), tone: 'success', weight: 1.4 },
            { label: 'Entries', value: report.entries, tone: 'info' },
            ...(report.reversed > 0 ? [{ label: 'Reversed', value: report.reversed, tone: 'neutral' as const }] : []),
          ]}
        />
        {report.rows.length === 0
          ? <Text style={styles.empty}>No revenue matches these filters.</Text>
          : <DataTable columns={columns} rows={report.rows} zebra compact small />}
      </Page>
    </Document>
  );
};

export default RevenueReportDocument;
